import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ArrowRight, RefreshCw } from "lucide-react";
import SEO from "@/components/SEO";
import { api, resolveImage } from "@/lib/api";

// 3-step gift wizard → curated 2-3 product bundle. Uses tags on products
// (badge/notes) and price to filter. When someone completes it we display a
// bundle they can add to cart in one click.
const STEPS = [
  {
    q: "Who's it for?",
    options: [
      { label: "A guy in your life", key: "for-him" },
      { label: "A woman in your life", key: "for-her" },
      { label: "A couple / housewarming", key: "couple" },
      { label: "Yourself. Treat yourself.", key: "self" },
    ],
  },
  {
    q: "What's the vibe?",
    options: [
      { label: "Sweet & flirty", key: "sweet" },
      { label: "Sexy & bold", key: "sexy" },
      { label: "Cozy & warm", key: "cozy" },
      { label: "Smooth & grown", key: "grown" },
    ],
  },
  {
    q: "Budget?",
    options: [
      { label: "Under $10 · one-off", key: "solo" },
      { label: "$15–$25 · a real gift", key: "bundle" },
      { label: "Go bigger · buy 2 get 1 free ($18 + free ship)", key: "bogo" },
    ],
  },
];

// Vibe → preferred product slugs (ranked). Non-listed products still show up if inventory allows.
const VIBE_MAP = {
  sweet: ["blush-and-bloom", "cherry-bomb", "freshly-baked"],
  sexy: ["cherry-bomb", "sugared-skin", "deep-blue"],
  cozy: ["freshly-baked", "sugared-skin", "blush-and-bloom"],
  grown: ["bare-wood", "deep-blue", "sugared-skin"],
};

export default function GiftFinder() {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({});
  const [products, setProducts] = useState([]);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => setProducts([])); }, []);

  const done = step >= STEPS.length;

  const bundle = useMemo(() => {
    if (!done || !products.length) return [];
    const ranked = VIBE_MAP[picks.vibe] || products.map((p) => p.id);
    const count = picks.budget === "solo" ? 1 : picks.budget === "bogo" ? 3 : 2;
    // pick top `count` products by rank
    const ordered = ranked
      .map((id) => products.find((p) => p.id === id))
      .filter(Boolean);
    const filler = products.filter((p) => !ordered.find((o) => o.id === p.id));
    return [...ordered, ...filler].slice(0, count);
  }, [done, products, picks]);

  const price = useMemo(() => bundle.reduce((s, p) => s + (p.price || 0), 0), [bundle]);
  const bogoDiscount = picks.budget === "bogo" && bundle.length === 3 ? Math.min(...bundle.map((p) => p.price || 0)) : 0;
  const total = Math.max(0, price - bogoDiscount);

  const restart = () => { setStep(0); setPicks({}); };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Booty Breeze Gift Finder",
    "description": "Pick the perfect adult air-freshener gift bundle in 3 questions.",
    "url": "https://bootybreeze.com/gift-finder",
    "step": STEPS.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.q,
      "text": s.options.map((o) => o.label).join("; "),
    })),
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16" data-testid="gift-finder-page">
      <SEO
        title="Gift Finder — Perfect Adult Car Freshener Bundle in 3 Steps"
        description="Answer 3 quick questions and we'll pair you with a curated bundle of Booty Breeze scents. Under $25, ships free over $10, actually lands the joke."
        canonical="/gift-finder"
        jsonLd={jsonLd}
      />
      <div className="mb-6"><Link to="/" data-testid="gift-home-link" className="text-white/50 hover:text-white text-xs uppercase tracking-widest">← Home</Link></div>
      <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3 flex items-center gap-2"><Gift size={14} /> Gift finder</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mb-4">The perfect gift, in 30 seconds.</h1>
      <p className="text-white/60 text-base mb-10 max-w-xl">Answer 3 questions. We build the bundle. Free shipping over $10.</p>

      {!done && (
        <div className="rounded-2xl border border-white/10 bg-[#0f0f0f] p-6 sm:p-10" data-testid={`gift-step-${step}`}>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-3">Step {step + 1} of {STEPS.length}</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">{STEPS[step].q}</h2>
          <div className="grid gap-3">
            {STEPS[step].options.map((opt, i) => (
              <button key={opt.key}
                data-testid={`gift-option-${step}-${i}`}
                onClick={() => {
                  const key = step === 0 ? "recipient" : step === 1 ? "vibe" : "budget";
                  setPicks({ ...picks, [key]: opt.key });
                  setStep(step + 1);
                }}
                className="text-left px-5 py-4 rounded-xl border border-white/10 bg-white/[.02] hover:border-[#FF3D8B] hover:bg-[#FF3D8B]/5 text-white transition-all group flex items-center justify-between">
                <span>{opt.label}</span>
                <ArrowRight size={16} className="text-white/30 group-hover:text-[#FF3D8B]" />
              </button>
            ))}
          </div>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} data-testid="gift-back-btn"
              className="mt-6 text-white/40 hover:text-white text-xs uppercase tracking-widest">← Back</button>
          )}
        </div>
      )}

      {done && (
        <div className="rounded-2xl border border-white/10 bg-[#0f0f0f] p-8 sm:p-10" data-testid="gift-result">
          <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3">Your bundle</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-6">
            {bundle.length} {bundle.length === 1 ? "scent" : "scents"} · <span className="text-[#FF3D8B]">${total.toFixed(2)}</span>
            {bogoDiscount > 0 && <span className="text-white/40 text-lg ml-2 line-through">${price.toFixed(2)}</span>}
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {bundle.map((p) => (
              <Link to={`/product/${p.id}`} key={p.id} data-testid={`gift-bundle-item-${p.id}`}
                className="rounded-xl border border-white/10 bg-white/[.02] p-4 hover:border-[#FF3D8B]/40 transition-all">
                <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-24 object-contain mb-3" />
                <div className="text-white text-sm font-semibold">{p.name}</div>
                <div className="text-white/50 text-xs">${p.price?.toFixed(2)}</div>
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/shop" data-testid="gift-shop-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0]">
              Grab this bundle <ArrowRight size={14} />
            </Link>
            <button onClick={restart} data-testid="gift-restart-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 hover:text-white text-xs uppercase tracking-widest">
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
